import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import SmtpConfigModal from "../../components/SmtpConfigModal";

import api from "../../services/api";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ConfirmationModal from "../../components/ConfirmationModal";

const useStyles = makeStyles(theme => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    customTableCell: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
}));

const SmtpConfigs = () => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [smtpConfigs, setSmtpConfigs] = useState([]);
    const [smtpConfigModalOpen, setSmtpConfigModalOpen] = useState(false);
    const [selectedSmtpConfig, setSelectedSmtpConfig] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [deletingSmtpConfig, setDeletingSmtpConfig] = useState(null);

    useEffect(() => {
        fetchSmtpConfigs();
    }, []);

    const fetchSmtpConfigs = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/smtp-configs");
            setSmtpConfigs(data);
        } catch (err) {
            toastError(err);
        }
        setLoading(false);
    };

    const handleOpenSmtpConfigModal = () => {
        setSelectedSmtpConfig(null);
        setSmtpConfigModalOpen(true);
    };

    const handleCloseSmtpConfigModal = () => {
        setSelectedSmtpConfig(null);
        setSmtpConfigModalOpen(false);
    };

    const handleEditSmtpConfig = (smtpConfig) => {
        setSelectedSmtpConfig(smtpConfig);
        setSmtpConfigModalOpen(true);
    };

    const handleDeleteSmtpConfig = async (smtpConfigId) => {
        try {
            await api.delete(`/smtp-configs/${smtpConfigId}`);
            toast.success("Configuração SMTP deletada com sucesso!");
            fetchSmtpConfigs();
        } catch (err) {
            toastError(err);
        }
        setDeletingSmtpConfig(null);
    };

    const handleTestConnection = async (smtpConfigId) => {
        try {
            const { data } = await api.post(`/smtp-configs/${smtpConfigId}/test`);
            if (data.success) {
                toast.success(data.message);
            } else {
                toast.error(data.message);
            }
        } catch (err) {
            toastError(err);
        }
    };

    return (
        <MainContainer>
            <ConfirmationModal
                title="Deletar Configuração SMTP"
                open={confirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                onConfirm={() => handleDeleteSmtpConfig(deletingSmtpConfig)}
            >
                Tem certeza que deseja deletar esta configuração SMTP?
            </ConfirmationModal>
            <SmtpConfigModal
                open={smtpConfigModalOpen}
                onClose={handleCloseSmtpConfigModal}
                aria-labelledby="form-dialog-title"
                smtpConfigId={selectedSmtpConfig?.id}
                onSave={fetchSmtpConfigs}
            />
            <MainHeader>
                <Title>Configurações SMTP</Title>
                <MainHeaderButtonsWrapper>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenSmtpConfigModal}
                    >
                        Nova Configuração
                    </Button>
                </MainHeaderButtonsWrapper>
            </MainHeader>
            <Paper className={classes.mainPaper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell align="center">Nome</TableCell>
                            <TableCell align="center">Host</TableCell>
                            <TableCell align="center">Porta</TableCell>
                            <TableCell align="center">Criptografia</TableCell>
                            <TableCell align="center">Remetente</TableCell>
                            <TableCell align="center">Tipo</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="center">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <>
                            {smtpConfigs.map((config) => (
                                <TableRow key={config.id}>
                                    <TableCell align="center">{config.name}</TableCell>
                                    <TableCell align="center">{config.host}</TableCell>
                                    <TableCell align="center">{config.port}</TableCell>
                                    <TableCell align="center">{config.encryption}</TableCell>
                                    <TableCell align="center">{config.fromEmail}</TableCell>
                                    <TableCell align="center">
                                        {config.isGlobal ? (
                                            <Chip
                                                label="Global"
                                                color="primary"
                                                size="small"
                                            />
                                        ) : (
                                            <Chip
                                                label={config.user?.name || "Usuário"}
                                                size="small"
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {config.isActive ? (
                                            <CheckCircleIcon style={{ color: "green" }} />
                                        ) : (
                                            <CancelIcon style={{ color: "red" }} />
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Testar Conexão">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleTestConnection(config.id)}
                                            >
                                                <PlayArrowIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Editar">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditSmtpConfig(config)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Deletar">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setDeletingSmtpConfig(config.id);
                                                    setConfirmModalOpen(true);
                                                }}
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {smtpConfigs.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography variant="body2">
                                            Nenhuma configuração SMTP encontrada
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </>
                    </TableBody>
                </Table>
            </Paper>
        </MainContainer>
    );
};

export default SmtpConfigs;
